import React, { useEffect, useState } from "react";

import { ordersService } from "api/services/orders.service";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import Modal from "components/UI/Modal";
import { getApiErrorMessage } from "utils/api-error";

import type { AdminOrder, FulfillmentUpdatableStatus } from "api/types/order.types";

const FULFILLMENT_OPTIONS: { value: FulfillmentUpdatableStatus; label: string }[] = [
  { value: "PREPARING", label: "배송준비" },
  { value: "IN_TRANSIT", label: "배송중" },
  { value: "DELIVERED", label: "배송완료" },
];

interface FulfillmentFormState {
  fulfillment_status: FulfillmentUpdatableStatus | "";
  tracking_number: string;
  courier: string;
}

const buildInitialForm = (order: AdminOrder | null): FulfillmentFormState => {
  if (!order) return { fulfillment_status: "", tracking_number: "", courier: "" };
  const current = order.fulfillment_status;
  const isUpdatable = current === "PREPARING" || current === "IN_TRANSIT" || current === "DELIVERED";
  return {
    fulfillment_status: isUpdatable ? (current as FulfillmentUpdatableStatus) : "",
    tracking_number: order.tracking_number ?? "",
    courier: order.courier ?? "",
  };
};

interface FulfillmentModalProps {
  open: boolean;
  order: AdminOrder | null;
  onClose: () => void;
  onUpdated?: () => void;
}

const FulfillmentModal: React.FC<FulfillmentModalProps> = ({ open, order, onClose, onUpdated }) => {
  const [form, setForm] = useState<FulfillmentFormState>(() => buildInitialForm(order));
  const [saving, setSaving] = useState(false);

  // 모달이 열릴 때마다 대상 주문 기준으로 폼 초기화
  useEffect(() => {
    if (open) setForm(buildInitialForm(order));
  }, [open, order]);

  const handleSubmit = async () => {
    if (!order || !form.fulfillment_status) return;
    setSaving(true);
    try {
      await ordersService.updateFulfillment(order.id, {
        fulfillment_status: form.fulfillment_status,
        ...(form.tracking_number ? { tracking_number: form.tracking_number } : {}),
        ...(form.courier ? { courier: form.courier } : {}),
      });
      onUpdated?.();
      onClose();
    } catch (err) {
      alert(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={`배송 상태 변경 — ${order?.order_number ?? ""}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            취소
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving || !form.fulfillment_status}>
            {saving ? "저장 중..." : "변경 저장"}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Select
          label="배송 상태"
          value={form.fulfillment_status}
          onChange={(e) =>
            setForm((p) => ({
              ...p,
              fulfillment_status: e.target.value as FulfillmentUpdatableStatus | "",
            }))
          }
          options={[{ value: "", label: "선택" }, ...FULFILLMENT_OPTIONS]}
        />
        <Input label="택배사 (배송중 선택 시 필수 입력)" placeholder="예: CJ대한통운" value={form.courier} onChange={(e) => setForm((p) => ({ ...p, courier: e.target.value }))} />
        <Input label="운송장 번호 (배송중 선택 시 필수 입력)" placeholder="예: 1234567890" value={form.tracking_number} onChange={(e) => setForm((p) => ({ ...p, tracking_number: e.target.value }))} />
      </div>
    </Modal>
  );
};

export default FulfillmentModal;
